import { Injectable, OnDestroy } from "@angular/core";
import {
  Observable,
  Observer,
  SubscriptionLike,
  Subject,
  interval,
} from "rxjs";
import {
  WebSocketSubjectConfig,
  WebSocketSubject,
  webSocket,
} from "rxjs/webSocket";
import { environment } from "../../../environments/environment";
import { share, distinctUntilChanged, takeWhile } from "rxjs/operators";
import { NGXLogger } from "ngx-logger";

export interface IWsMessage<T> {
  dаta: T;
}

export interface IWebsocketService {
  send(data: any): void;

  status: Observable<boolean>;
}

const RECONNECT_INTERVAL = 30000;

@Injectable({ providedIn: "root" })
export class WebsocketService implements IWebsocketService, OnDestroy {
  private config: WebSocketSubjectConfig<IWsMessage<any>>;

  private reconnection$: Observable<number>;
  private webSocket$: WebSocketSubject<IWsMessage<any>>;
  private connection$: Observer<boolean>;

  public status: Observable<boolean>;
  public isConnected: boolean;

  private websocketSub: SubscriptionLike;
  private statusSub: SubscriptionLike;
  public wsMessages$: Subject<IWsMessage<any>>;

  private createWebSocketSubject = webSocket;

  constructor(private logger: NGXLogger) {
    this.wsMessages$ = new Subject<IWsMessage<any>>();

    this.config = {
      url: environment.api_url_homing_pigeon,
      closeObserver: {
        next: () => {
          this.logger.info("WebSocket closed!");
          this.webSocket$ = null;
          this.connection$.next(false);
        },
      },
      openObserver: {
        next: () => {
          this.logger.info("WebSocket connected!");
          this.connection$.next(true);
        },
      },
    };

    this.status = new Observable<boolean>((observer) => {
      this.connection$ = observer;
    }).pipe(share(), distinctUntilChanged());

    // run reconnect if not connection
    this.statusSub = this.status.subscribe((isConnected) => {
      this.isConnected = isConnected;
      if (
        !this.reconnection$ &&
        typeof isConnected === "boolean" &&
        !isConnected
      ) {
        this.reconnect();
      }
    });

    this.websocketSub = this.wsMessages$.subscribe(null, (error: ErrorEvent) =>
      console.error("WebSocket error!", error),
    );
  }

  ngOnDestroy() {
    this.websocketSub.unsubscribe();
    this.statusSub.unsubscribe();
  }

  private handleMessage = (message: any) => {
    this.wsMessages$.next(message);
  };

  private handleError = (error: Event) => {
    this.logger.error(`Websocket error event occurred: ${error.type}`);
    if (!this.webSocket$) {
      // run reconnect if errors
      this.reconnect();
    }
  };

  private handleCompletion = () => {
    this.logger.info("WebSocket Subscription Complete!");
  };

  // Connect to WebSocket
  public connect(): WebSocketSubject<IWsMessage<any>> {
    this.webSocket$ = this.createWebSocketSubject(this.config);

    this.webSocket$.subscribe(
      this.handleMessage,
      this.handleError,
      this.handleCompletion,
    );

    return this.webSocket$;
  }

  // reconnect if not connecting or errors
  private reconnect(): void {
    this.reconnection$ = interval(RECONNECT_INTERVAL).pipe(
      takeWhile(() => !this.webSocket$),
    );

    this.reconnection$.subscribe(
      () => this.connect(),
      null,
      () => {
        // Subject complete if reconnect attemts ending
        this.reconnection$ = null;

        if (!this.webSocket$) {
          this.wsMessages$.complete();
          this.connection$.complete();
        }
      },
    );
  }

  //send messages to server
  public send(data: any): void {
    if (this.isConnected) {
      this.webSocket$.next(<any>data);
    } else {
      this.logger.warn("Attempted to send to unconnected websocket");
    }
  }
}
